import { UserDb } from 'lib/cosmos/user/userDb.js';
import { unitConversionHelper } from 'lib/helpers/unit-conversion-helper.class.js';
import { map, of } from 'rxjs';
import { CoreAppComponentDependencies } from 'src/app/core/core-app-dependencies.js';
import { ListAdminUserTemplatesComponent } from 'src/app/modules/users/admin/list-admin-user-templates.component.js';
import {
	IListAdminComponentConfig,
	ListAdminUsersComponent
} from 'src/app/modules/users/admin/list-admin-users.component.js';
import { ViewAdminUserComponent } from 'src/app/modules/users/admin/view-admin-user.component.js';
import {
	ICoreRoutes,
	IPageMetadata,
	IPageTag,
	ISharedData,
	ITypePageCard,
	ITypeParentRoute,
	ITypeRoute,
	ITypeRouteAction,
	RouteInitializer
} from '../../pages/type-routes/type-route.models.js';

export interface IAdminUserRoutes extends ICoreRoutes {
	users: ITypeRoute<
		undefined,
		{
			list: ITypeRouteAction<IListAdminComponentConfig>;
			trainersList: ITypeRouteAction<IListAdminComponentConfig>;
			clientsLists: ITypeRouteAction<IListAdminComponentConfig>;
			premiumLists: ITypeRouteAction<IListAdminComponentConfig>;
			referrersList: ITypeRouteAction<IListAdminComponentConfig>;
			expiredUsersList: ITypeRouteAction<IListAdminComponentConfig>;
			demoClientTemplatesList: ITypeRouteAction<undefined>;
		}
	>;
	user: ITypeRoute<
		UserDb.IUserItem,
		{
			view: ITypeRouteAction<UserDb.IUserItem>;
		}
	>;
}

export const adminUserRoutes: RouteInitializer<IAdminUserRoutes> = {
	users: {
		mainPath: 'admin',
		uniqueId: 'adminUsers',
		parentRoutes: (context) => [],
		loadSharedData: (context) => {
			return of(undefined);
		},
		menuItems: (context) => [
			{
				title: `All users`,
				routerLink: context.dependencies.navigateService
					.actionRoute((routes) => routes.adminUserRoutes.users.children.list)
					.getUrl(),
				iconType: 'users'
			},
			{
				title: `Trainers`,
				routerLink: context.dependencies.navigateService
					.actionRoute((routes) => routes.adminUserRoutes.users.children.trainersList)
					.getUrl(),
				iconType: 'users'
			},
			{
				title: `Clients`,
				routerLink: context.dependencies.navigateService
					.actionRoute((routes) => routes.adminUserRoutes.users.children.clientsLists)
					.getUrl(),
				iconType: 'users'
			},
			{
				title: `Premium`,
				routerLink: context.dependencies.navigateService
					.actionRoute((routes) => routes.adminUserRoutes.users.children.premiumLists)
					.getUrl(),
				iconType: 'purchase'
			},
			{
				title: `Expired trial`,
				routerLink: context.dependencies.navigateService
					.actionRoute((routes) => routes.adminUserRoutes.users.children.expiredUsersList)
					.getUrl(),
				iconType: 'purchase'
			},
			{
				title: `Referrers`,
				routerLink: context.dependencies.navigateService
					.actionRoute((routes) => routes.adminUserRoutes.users.children.referrersList)
					.getUrl(),
				iconType: 'referrer'
			},
			{
				title: `User Templates`,
				routerLink: context.dependencies.navigateService
					.actionRoute((routes) => routes.adminUserRoutes.users.children.demoClientTemplatesList)
					.getUrl(),
				iconType: 'user_pen'
			}
		],
		children: {
			list: {
				uniqueId: `adminUsersList`,
				component: ListAdminUsersComponent,
				standaloneSharedData: (context) => {
					const sharedData: ISharedData<IListAdminComponentConfig> = {
						linkedItems: [],
						data: {
							filter: 'all'
						}
					};

					return sharedData;
				},
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: `All Users`
					};
					return metadata;
				}
			},

			trainersList: {
				uniqueId: `trainersList`,
				component: ListAdminUsersComponent,
				standaloneSharedData: (context) => {
					const sharedData: ISharedData<IListAdminComponentConfig> = {
						linkedItems: [],
						data: {
							filter: 'trainers'
						}
					};

					return sharedData;
				},
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: `Trainers`
					};
					return metadata;
				}
			},
			clientsLists: {
				uniqueId: `clientsLists`,
				component: ListAdminUsersComponent,
				standaloneSharedData: (context) => {
					const sharedData: ISharedData<IListAdminComponentConfig> = {
						linkedItems: [],
						data: {
							filter: 'clients'
						}
					};

					return sharedData;
				},
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: `Clients`
					};
					return metadata;
				}
			},
			premiumLists: {
				uniqueId: `premiumLists`,
				component: ListAdminUsersComponent,
				standaloneSharedData: (context) => {
					const sharedData: ISharedData<IListAdminComponentConfig> = {
						linkedItems: [],
						data: {
							filter: 'premium'
						}
					};

					return sharedData;
				},
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: `Premium`
					};
					return metadata;
				}
			},
			referrersList: {
				uniqueId: `referrersList`,
				component: ListAdminUsersComponent,
				standaloneSharedData: (context) => {
					const sharedData: ISharedData<IListAdminComponentConfig> = {
						linkedItems: [],
						data: {
							filter: 'referrers'
						}
					};

					return sharedData;
				},
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: `Referrers users`
					};
					return metadata;
				}
			},
			expiredUsersList: {
				uniqueId: `expiredUsersList`,
				component: ListAdminUsersComponent,
				standaloneSharedData: (context) => {
					const sharedData: ISharedData<IListAdminComponentConfig> = {
						linkedItems: [],
						data: {
							filter: 'expiredUsers'
						}
					};

					return sharedData;
				},
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: `Expired users`
					};
					return metadata;
				}
			},
			demoClientTemplatesList: {
				uniqueId: `adminUserTemplates`,
				component: ListAdminUserTemplatesComponent,
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: `User templates`
					};
					return metadata;
				}
			}
		}
	},
	user: {
		mainPath: 'admin',
		uniqueId: 'adminUser',
		parentRoutes: (context) => {
			const routes: ITypeParentRoute[] = [];

			return [
				{
					routerLink: context.dependencies.navigateService
						.actionRoute((routes) => routes.adminUserRoutes.users.children.list)
						.getUrl(),
					title: `Users`
				},
				...routes
			];
		},
		loadSharedData: (context) => {
			return context.dependencies.httpService
				.get<UserDb.IUserItem>((builder) =>
					builder.withCustomApiRoute('user', 'getUser').withId(context.routeData.primaryId).build()
				)
				.pipe(
					map((response) => {
						const shared: ISharedData<UserDb.IUserItem> = {
							data: response.data,
							linkedItems: response.linkedItems ?? []
						};

						return shared;
					})
				);
		},
		menuItems: (context) => [],
		children: {
			view: {
				uniqueId: `adminViewUser`,
				component: ViewAdminUserComponent,
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: data.sharedData?.data.fullName ?? 'View',
						card: getUserCard(data.sharedData?.data, data.dependencies)
					};
					return metadata;
				}
			}
		}
	}
};

function getUserCard(
	user: UserDb.IUserItem | undefined,
	dependencies: CoreAppComponentDependencies
): ITypePageCard | undefined {
	if (!user) {
		return undefined;
	}

	const tags: IPageTag[] = [];
	const authUser = dependencies.authService.authUser();

	if (!dependencies.screenService.currentScreen().isMobileView) {
		tags.push({
			icon: 'email',
			text: user.email
		});
	}

	if (user.preferredUnit) {
		if (user.heightCm) {
			tags.push({
				icon: 'height',
				text: unitConversionHelper.heightInPreferredUnit(authUser?.preferredUnit, user.heightCm)
			});
		}

		if (user.weightKg) {
			tags.push({
				icon: 'weight',
				text: unitConversionHelper.weightInPreferredUnit(authUser?.preferredUnit, user.weightKg)
			});
		}
	}

	if (user.timezone) {
		tags.push({
			icon: 'timezone',
			text: `${user.timezone}`
		});
	}

	let lastActiveText: string = `Client hasn't been active yet`;

	if (user.lastActiveDate) {
		lastActiveText = `Last active ${dependencies.timeService.fromNow(user.lastActiveDate)}`;
	}

	const card: ITypePageCard = {
		title: { value: user.fullName },
		avatar: {
			avatar: user.avatar,
			avatarName: user.fullName
		},
		subtitle: lastActiveText,
		tags: tags
	};

	return card;
}
